import axios, { AxiosResponse } from "axios";
import { delay, putResolve, select } from "redux-saga/effects";
import {
  COMPLETE_BUY_AIR,
  DisruptionOptInProperties,
  DISRUPTION_SOURCE,
  UncategorizedPurchaseError,
  PriceDropProtectionEnum,
  ITrackingProperties,
  ChfarAirOrchestratorEvent,
} from "redmond";
import {
  TravelItineraryEnum,
  PurchaseResult,
  SingleTravelItinerary,
  MultiTravelItinerary,
} from "@b2bportal/air-booking-api";
import {
  FulfillFailure,
  FulfillResponse,
  FulfillResponseEnum,
  FulfillSuccess,
  FulfillSuccessV0,
  Product,
  purchaseApi,
} from "@b2bportal/purchase-api";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { brexBridge, getIsBrexApp } from "b2b-base/src/brex/bridge";
import { buildFlightBookingData } from "../../utils/buildFlightBookingData";
import { getCardDeclineReasonFromErrors } from "@capone/checkout";
import { IStoreState } from "../../../../reducers/types";
import {
  getCompleteBuyAirProperties,
  getCompleteBuyTravelOfferProperties,
  getPriceDropProperties,
  getPurchaseError,
  getAgentErrorTitleFromPurchaseError,
  hasActiveRefundableFareInFlightBookSelector,
  getSession,
} from "../../reducer";
import {
  refundableFaresPropertiesSelector,
  cfarDiscountPropertiesSelector,
  predictionSelector,
  getCfarAttachProperties,
  chfarOffersTrackingPropertiesSelector,
  selectedChfarIdSelector,
  flightsByFlightShopTypeSelector,
} from "../../../shop/reducer";
import { getTripCategory } from "../../../search/reducer";
import {
  hasSMSBeenSelected,
  hasAppNotifBeenSelected,
  hasDisruptionProtectionBeenAttachedSelector,
  chfarExerciseTrackingPropertiesSelector,
} from "../../../book/reducer/selectors";
import Logger from "../../../../helpers/Logger";
import {
  setPollFinalizedCallStateFailure,
  setPollFinalizedCallStateSuccess,
  setFinalized,
  IPollFinalized,
} from "../../actions/actions";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { isDisruptionOptInEnabledSelector } from "../../../ancillary/reducer";
import { DO_NOT_APPLY_CHFAR_OPTION } from "../../../shop/components/addOnComponents/helpers";
import dayjs from "dayjs";


export function* pollFinalizedSaga({ initialTime }: IPollFinalized) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const completeBuyAirProperties = getCompleteBuyAirProperties(state);
    const completeBuyTravelOfferProperties =
      getCompleteBuyTravelOfferProperties(state);
    const priceDropProperties = getPriceDropProperties(state);
    const hasDisruptionBeenAttached =
      hasDisruptionProtectionBeenAttachedSelector(state);
    const isDisruptionOptInEnabled = isDisruptionOptInEnabledSelector(state);
    const disruptionNotificationOptInProperties: DisruptionOptInProperties =
      hasDisruptionBeenAttached && isDisruptionOptInEnabled
        ? {
            messaging_source: DISRUPTION_SOURCE,
            messaging_channel_preference_sms: hasSMSBeenSelected(state),
            messaging_channel_preference_app_notif:
              hasAppNotifBeenSelected(state),
          }
        : {};

    const prediction = predictionSelector(state);
    const predictionProperties: ITrackingProperties = {
      properties: {},
      encryptedProperties: [
        prediction?.priceDropProtection?.trackingPropertiesV2
          ?.encryptedProperties ?? "",
      ],
    };
    const chfarExerciseTrackingProperties =
      chfarExerciseTrackingPropertiesSelector(state);
    if (!sessionToken) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];

    let pollTerminated = false;
    let index = 0;

    const request = () =>
      purchaseApi(axios as any).apiV0PurchaseFulfillPollPost(sessionToken);

    while (!pollTerminated) {
      yield delay(delayTimes[index]);

      const finalizedCheckedResponse: AxiosResponse<FulfillResponse> =
        yield request().catch((e) => {
          // [CMKT-1150] do not error out on network errors, instead retry
          console.error("Poll finalized flight failed", e);
          return Promise.resolve({
            data: { FulfillResponse: FulfillResponseEnum.Pending },
            status: 200,
            statusText: "Gateway timeout",
            headers: [],
            config: {},
          });
        });

      const fulfillRespnse = finalizedCheckedResponse.data;

      switch (fulfillRespnse.FulfillResponse) {
        case FulfillResponseEnum.Failure:
          pollTerminated = true;
          const failedResponse = fulfillRespnse as FulfillFailure;
          const errors = failedResponse.errors.map(getPurchaseError);

          if (failedResponse.errors.length > 0) {
            yield putResolve(setPollFinalizedCallStateFailure(errors));
            const cardDeclineProps = getCardDeclineReasonFromErrors(failedResponse.errors);
            trackEvent({
              eventName: COMPLETE_BUY_AIR,
              properties: {
                ...completeBuyAirProperties,
                ...completeBuyTravelOfferProperties.properties,
                price_bucket: priceDropProperties.price_bucket,
                failure_reason: errors
                  .map((error) => {
                    if ((error as UncategorizedPurchaseError)?.code) {
                      return (error as UncategorizedPurchaseError)?.code;
                    }
                    return getAgentErrorTitleFromPurchaseError(error);
                  })
                  .join(" - "),
                ...cardDeclineProps,
                success: false,
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
              encryptedProperties: [
                ...completeBuyTravelOfferProperties.encryptedProperties,
              ],
            });
            if (chfarExerciseTrackingProperties) {
              trackEvent({
                eventName:
                  ChfarAirOrchestratorEvent.CHFAR_EXERCISE_COMPLETE_FAIL,
                properties: {
                  ...chfarExerciseTrackingProperties.properties,
                  chfar_exercise_fail_reason: errors,
                  ...cardDeclineProps,
                },
                encryptedProperties: [
                  ...(chfarExerciseTrackingProperties?.encryptedProperties ??
                    []),
                ],
              });
            }
          } else {
            throw new Error(
              "Poll Finalized response returned an error and the given error code is not handleable.",
            );
          }

          return;
        case FulfillResponseEnum.Pending:
          break;
        case FulfillResponseEnum.Success:
          pollTerminated = true;
          const finalizedResponse = fulfillRespnse as
            | FulfillSuccess
            | FulfillSuccessV0;
          yield putResolve(setPollFinalizedCallStateSuccess());

          switch ((finalizedResponse as FulfillSuccessV0).fulfillment.type) {
            case Product.Flight:
              const flightPurchaseFinalized = (
                finalizedResponse as FulfillSuccessV0
              ).fulfillment.value as PurchaseResult;
              const chfarPurchaseTrackingProperties =
                flightPurchaseFinalized.itinerary.chfarTrackingProperties;
              yield putResolve(
                setFinalized(Product.Flight, flightPurchaseFinalized),
              );

              if (getIsBrexApp()) {
                const flightConfirmationId =
                  flightPurchaseFinalized.itinerary.travelItinerary.locators
                    ?.agent.unscopedValue;
                if (flightConfirmationId) {
                  brexBridge.emit("BOOKING_COMPLETE", {
                    reservationId: flightConfirmationId,
                  });
                }
              }

              const trip = flightPurchaseFinalized.itinerary.travelItinerary;

              const { cfar_fare_choice } =
                refundableFaresPropertiesSelector(state);
              const { cfar_choice } = getCfarAttachProperties(state);

              const hasActiveRefundableFare =
                hasActiveRefundableFareInFlightBookSelector(state);
              const cfarDiscountProperties =
                cfarDiscountPropertiesSelector(state);

              const selectedChfarId = selectedChfarIdSelector(state);
              const chfar_choice =
                selectedChfarId !== null
                  ? selectedChfarId.quoteId !==
                    DO_NOT_APPLY_CHFAR_OPTION.quoteId
                  : undefined;
              const chfarOffersTrackingProps =
                chfarOffersTrackingPropertiesSelector(state);
              trackEvent({
                eventName: COMPLETE_BUY_AIR,
                properties: {
                  ...completeBuyAirProperties,
                  ...completeBuyTravelOfferProperties.properties,
                  ...disruptionNotificationOptInProperties,
                  ...chfarOffersTrackingProps?.properties,
                  ...chfarPurchaseTrackingProperties?.properties,
                  price_bucket: priceDropProperties.price_bucket,
                  success: true,
                  provider: trip?.locators?.agent.provider ?? "",
                  agent_locator:
                    trip?.TravelItinerary ===
                      TravelItineraryEnum.MultiTravelItinerary &&
                    trip?.locators?.agent.provider === "multiProvider"
                      ? flightPurchaseFinalized.itinerary.travelItinerary
                          .locators?.agent.value
                      : flightPurchaseFinalized.itinerary.travelItinerary
                          .locators?.agent.unscopedValue,
                  cfar_fare_choice:
                    cfar_fare_choice ??
                    (hasActiveRefundableFare ? 1 : undefined),
                  cfar_discount_original_premium:
                    cfarDiscountProperties.original_premium,
                  cfar_discount_percentage:
                    cfarDiscountProperties.discount_percentage,
                  cfar_choice,
                  price_drop_added:
                    flightPurchaseFinalized.itinerary.priceDropEligibility
                      .PriceDropProtection ===
                    PriceDropProtectionEnum.IsEligible,
                  evaluated_recommendation: prediction?.recommendationV2,
                  chfar_choice,
                  booking_duration: dayjs().diff(initialTime, "seconds", true),
                  merchant:
                    flightPurchaseFinalized?.itinerary.isHopperMor !==
                      undefined &&
                    !flightPurchaseFinalized?.itinerary.isHopperMor
                      ? "airline"
                      : "hopper",
                },

                encryptedProperties: [
                  ...completeBuyTravelOfferProperties.encryptedProperties,
                  ...predictionProperties.encryptedProperties,
                  ...(chfarOffersTrackingProps?.encryptedProperties || []),
                  ...(chfarPurchaseTrackingProperties?.encryptedProperties ||
                    []),
                ],
              });
              const flightBookingData = buildFlightBookingData(
                trip as SingleTravelItinerary | MultiTravelItinerary,
                flightPurchaseFinalized.itinerary.passengers,
                flightsByFlightShopTypeSelector(state)?.airports,
                getTripCategory(state),
              );

              travelAppInterface.sendEvent({
                name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
                funnel: TravelAppInterfaceFunnelType.FLIGHTS,
                data: flightBookingData,
              });
              if (chfarExerciseTrackingProperties) {
                trackEvent({
                  eventName: ChfarAirOrchestratorEvent.CHFAR_EXERCISE_COMPLETE,
                  properties: chfarExerciseTrackingProperties.properties,
                  encryptedProperties: [
                    ...(chfarExerciseTrackingProperties?.encryptedProperties ??
                      []),
                  ],
                });
              }
              break;
          }
      }

      // if we want to give up on polling we should instead direct the user to my trips
      if (index >= delayTimes.length) {
        pollTerminated = true;
        window.location.pathname = "/trips";
      }

      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch (e) {
    Logger.debug(e);
    yield putResolve(setPollFinalizedCallStateFailure([]));
    const state: IStoreState = yield select();
    const completeBuyAirProperties = getCompleteBuyAirProperties(state);
    const completeBuyTravelOfferProperties =
      getCompleteBuyTravelOfferProperties(state);
    const priceDropProperties = getPriceDropProperties(state);
    const chfarExerciseTrackingProperties =
      chfarExerciseTrackingPropertiesSelector(state);

    trackEvent({
      eventName: COMPLETE_BUY_AIR,
      properties: {
        ...completeBuyAirProperties,
        ...completeBuyTravelOfferProperties.properties,
        price_bucket: priceDropProperties.price_bucket,
        failure_reason: (e as Error).message,
      },
      encryptedProperties: [
        ...completeBuyTravelOfferProperties.encryptedProperties,
      ],
    });
    if (chfarExerciseTrackingProperties) {
      trackEvent({
        eventName: ChfarAirOrchestratorEvent.CHFAR_EXERCISE_COMPLETE_FAIL,
        properties: {
          ...chfarExerciseTrackingProperties.properties,
          chfar_exercise_fail_reason: (e as Error).message,
        },
        encryptedProperties: [
          ...(chfarExerciseTrackingProperties?.encryptedProperties ?? []),
        ],
      });
    }
  }
}
